<template>
  <BasicSkeleton :show-placeholder="!mappedCategoryList">
    <nav
      aria-label="Breadcrumb"
      class="breadcrumb-nav w-full"
    >
      <template v-if="mappedCategoryList">
        <ul class="breadcrumb flex flex-nowrap items-center whitespace-nowrap w-full min-w-0">
          <template v-if="isMobile || isTablet">
            <li class="ml-2">
              <NuxtLink
                aria-label="Startseite"
                :to="'/'"
                external
              >
                <BasicIcon
                  class="w-4 lg:order-1 leading-none text-paragraph-light"
                  :name="'house-outline'"
                />
              </NuxtLink>
            </li>
            <li
              class="ml-2"
              v-if="categoryForMobileView && Object.keys(categoryForMobileView).length > 0"
            >
              <NuxtLink :to="baseDir + categoryForMobileView.url">
                <span class="icon"> » </span>
                {{ categoryForMobileView.name }}
              </NuxtLink>
            </li>
          </template>
          <template v-else>
            <li class="nav-item shrink-0">
              <NuxtLink
                :to="'/'"
                class="nav-link"
                target="_self"
                :title="'Startseite'"
                aria-label="Startseite"
                external
              >
                <BasicIcon
                  class="w-4 lg:order-1 text-paragraph-light"
                  :name="'house-outline'"
                />
              </NuxtLink>
            </li>

            <li
              v-for="item in mappedCategoryList"
              :key="item.id"
              class="nav-item shrink-0"
            >
              <NuxtLink
                :to="baseDir + item.url"
                class="nav-link"
                target="_self"
                :title="item.name"
              >
                <span class="icon"> » </span>
                {{ item.name }}
              </NuxtLink>
            </li>

            <template v-if="articleTitle">
              <li
                class="nav-item last flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
                aria-current="page"
              >
                <span class="icon"> » </span>{{ articleTitle }}
              </li>
            </template>
          </template>
        </ul>
      </template>
    </nav>
    <template #placeholder>
      <SkeletonBreadcrumb />
    </template>
  </BasicSkeleton>
</template>

<script setup lang="ts">
import { useSchemaOrg } from '@unhead/schema-org/vue';
import type { MenuLink } from '~~/models/Navigation';

interface Props {
  categoryList: Array<MenuLink>;
  articleTitle?: string;
}

const props = defineProps<Props>();
const baseDir = inject('baseDir');
const { isMobile, isTablet } = useDevice();

const mappedCategoryList = computed(() => {
  const parentSlugs: Array<string> = [];
  return props.categoryList.map((category) => {
    const newCategory = { ...category };

    newCategory.url = [...parentSlugs, category?.url].join('/');
    parentSlugs.push(category?.url);

    return newCategory;
  });
});

const categoryForMobileView = computed(() => {
  if (isMobile || isTablet) {
    if (props.articleTitle) {
      return mappedCategoryList.value[mappedCategoryList.value.length - 1];
    } else if (mappedCategoryList?.value?.length >= 2) {
      return mappedCategoryList.value[mappedCategoryList.value.length - 2];
    }
  }
});

const schemaOrgBuilder = computed(() => {
  return new SchemaOrgBuilder().withCategoryList(mappedCategoryList.value).withArticleTitle(props.articleTitle);
});

// This useSchemaOrg needs to be precisely imported from @unhead/schema-org and not auto-imported from nuxt-schema-org EX-3032
useSchemaOrg([schemaOrgBuilder.value.buildBreadcrumb()], { tagDuplicateStrategy: 'replace' });
</script>
